import { format } from 'date-fns'

import { clone, getWeek } from '@/helpers/index'
import { JourneyType, JourneyType2, JourneyType3, RoundType } from '@/types/enum'

const ANDATA = 0
const RITORNO = 1

const _hasSamePlaces = (journey) => journey.origin.id === journey.destination.id

const ristrutturaViaggioStandard = (journey) => {
  const type =
    journey.type2 === JourneyType2.STANDARD_EMPTY
      ? JourneyType3.STANDARD_EMPTY
      : journey.type2 === JourneyType2.STANDARD_MONO
      ? JourneyType3.STANDARD_MONO
      : JourneyType3.STANDARD_MULTI

  return [clone({ ...journey, _meta: { ...journey._meta, journey_type: type } })]
}

const ristrutturaCircuito = (journey) => {
  const RACCOLTA = 0
  const DISTRIBUZIONE = 1

  const { origin, destination, distance_loaded, goods, type2, _meta } = journey

  if (_hasSamePlaces(journey) && type2 !== JourneyType2.ROUND_COLLECTION_AND_DISTRIBUTION) {
    return [
      clone({
        ...journey,
        _meta: {
          ..._meta,
          journey_type: JourneyType3.ROUND,
          round_type: type2.ROUND_COLLECTION ? RoundType.COLLECTION : RoundType.DISTRIBUTION,
        },
      }),
    ]
  }

  const journeys = [
    clone({
      ...journey,
      _meta: {
        ..._meta,
        journey_type: JourneyType3.ROUND,
      },
    }),
    // Ritorno/Distribuzione
    clone({
      ...journey,
      origin: destination,
      destination: origin,
      _meta: {
        ..._meta,
        journey_type: JourneyType3.ROUND,
      },
    }),
  ]

  // Prima DISTRIBUZIONE poi RACCOLTA
  switch (type2) {
    case JourneyType2.ROUND_COLLECTION_AND_DISTRIBUTION:
      journeys[ANDATA].distance_loaded = Math.round(distance_loaded / 2)
      journeys[ANDATA].goods = [null, goods[DISTRIBUZIONE]]
      journeys[ANDATA]._meta.round_type = RoundType.DISTRIBUTION
      journeys[RITORNO].distance_loaded = Math.round(distance_loaded / 2)
      journeys[RITORNO].goods = [goods[RACCOLTA], null]
      journeys[RITORNO]._meta.round_type = RoundType.COLLECTION
      break
    case JourneyType2.ROUND_COLLECTION:
      journeys[ANDATA].distance_empty = 0
      journeys[ANDATA].goods = [goods[RACCOLTA], null]
      journeys[ANDATA]._meta.round_type = RoundType.COLLECTION
      journeys[RITORNO].distance_loaded = 0
      journeys[RITORNO].stops_count = null
      journeys[RITORNO].stops_province_count = null
      journeys[RITORNO].goods = [null, null]
      journeys[RITORNO]._meta.round_type = null
      journeys[RITORNO]._meta.journey_type = JourneyType3.ROUND_OR_SHUTTLE_EMPTY
      break
    case JourneyType2.ROUND_DISTRIBUTION:
      journeys[ANDATA].distance_empty = 0
      journeys[ANDATA].goods = [null, goods[DISTRIBUZIONE]]
      journeys[ANDATA]._meta.round_type = RoundType.DISTRIBUTION
      journeys[RITORNO].distance_loaded = 0
      journeys[RITORNO].stops_count = null
      journeys[RITORNO].stops_province_count = null
      journeys[RITORNO].goods = [null, null]
      journeys[RITORNO]._meta.round_type = null
      journeys[RITORNO]._meta.journey_type = JourneyType3.ROUND_OR_SHUTTLE_EMPTY
      break

    default:
      throw new Error(`Journey type2 '${type2}' not found`)
  }

  return journeys
}

const ristrutturaNavetta = (journey) => {
  /*
    Se in una NAVETTA le destinazioni sono uguali
    e la merce trasportata in ANDATA e RITORNO è uguale per tipo, aspetto, pericolosità e peso,
    il viaggio non necessita di ristrutturazione.

    In tutti gli altri casi, la NAVETTA và ristrutturata come due viaggi NAVETTA:
    - ANDATA: Con la merce Raccolta
    - RITORNO: Con la merce Distribuita

    I Km percorsi saranno suddivisi equamente tra i due viaggi NAVETTA.
    Il numero di viaggi resterà invariato.

    Se non è stata trasportata merce in ANDATA o RITORNO, il corrispondente viaggio sarà a vuoto.
    */
  // Nel questionario
  const { type2, origin, destination, goods, distance_loaded, _meta } = journey

  const hasSamePlaces = _hasSamePlaces(journey)
  const hasSameGoods =
    goods[ANDATA]?.type === goods[RITORNO]?.type &&
    goods[ANDATA]?.dangerous === goods[RITORNO]?.dangerous &&
    goods[ANDATA]?.aspect === goods[RITORNO]?.aspect &&
    goods[ANDATA]?.weight === goods[RITORNO]?.weight

  if (hasSamePlaces && hasSameGoods)
    return [{ ...journey, _meta: { ..._meta, round_type: RoundType.SHUTTLE, journey_type: JourneyType3.SHUTTLE } }]

  const journeys = [
    // Andata
    clone({
      ...journey,
      _meta: { ..._meta, round_type: RoundType.SHUTTLE, journey_type: JourneyType3.SHUTTLE },
    }),
    // Ritorno
    clone({
      ...journey,
      origin: destination,
      destination: origin,
      _meta: { ..._meta, round_type: RoundType.SHUTTLE, journey_type: JourneyType3.SHUTTLE },
    }),
  ]

  switch (type2) {
    case JourneyType2.SHUTTLE_OUTWARD_AND_RETURN:
      journeys[ANDATA].goods = [goods[ANDATA], null]
      journeys[ANDATA].distance_loaded = Math.round(distance_loaded / 2)
      journeys[RITORNO].goods = [null, goods[RITORNO]]
      journeys[RITORNO].distance_loaded = Math.round(distance_loaded / 2)
      break
    case JourneyType2.SHUTTLE_ONLY_RETURN:
      journeys[ANDATA].goods = [null, null]
      journeys[ANDATA].distance_loaded = 0
      journeys[ANDATA]._meta.journey_type = JourneyType3.ROUND_OR_SHUTTLE_EMPTY
      journeys[RITORNO].goods = [null, goods[RITORNO]]
      journeys[RITORNO].distance_empty = 0
      break
    case JourneyType2.SHUTTLE_ONLY_OUTWARD:
      journeys[ANDATA].goods = [goods[ANDATA], null]
      journeys[ANDATA].distance_empty = 0
      journeys[RITORNO].goods = [null, null]
      journeys[RITORNO].distance_loaded = 0
      journeys[RITORNO]._meta.journey_type = JourneyType3.ROUND_OR_SHUTTLE_EMPTY
      break
    default:
      throw new Error(`Journey type2 '${type2}' not found`)
  }

  return journeys
}

const _computeVia = (journeys) => {
  let via = 0
  let sub = 0

  return journeys.map((j) => {
    const isMulti = j._meta.journey_type === JourneyType3.STANDARD_MULTI

    if (!isMulti || (isMulti && !j.parent_id)) {
      via++
      sub = 0
    }
    if (isMulti) sub++

    j._meta.via = isMulti ? `${via}.${sub}` : `${via}`

    return j
  })
}

export const ristrutturaViaggi = (survey) => {
  const restructuredJourneys = []

  const { days } = getWeek(survey.start_date, survey.end_date)

  survey?.journeys?.forEach((journey) => {
    switch (journey.type) {
      case JourneyType.STANDARD:
      case JourneyType.EMPTY:
        restructuredJourneys.push(...ristrutturaViaggioStandard(journey))
        break
      case JourneyType.SHUTTLE:
        restructuredJourneys.push(...ristrutturaNavetta(journey))
        break
      case JourneyType.ROUND:
        restructuredJourneys.push(...ristrutturaCircuito(journey))
        break
      default:
        throw `Journey type ${journey.type} not found`
    }
  })

  return _computeVia(restructuredJourneys).map((j) => {
    j._meta.date = days[j.day]
    return j
  })
}

export const standardJourneyToRow = (journey) => {
  const isClosed = (goodsItem, value) => (goodsItem?.is_inherited && goodsItem?.weights.onboard === 0 ? '–' : value)

  return {
    a: format(journey._meta.date, 'dd/MM'), // Data (gg/mm)
    b: journey.coupled_vehicle_load_capacity || '',
    c: journey.coupled_vehicle_axles || '',
    d: journey.origin.full_name,
    e: journey.origin_type,
    f: [0, 1, 2].map((i) => isClosed(journey?.goods?.[i], journey?.goods?.[i]?.type ?? '')),
    g: [0, 1, 2].map((i) => isClosed(journey?.goods?.[i], journey?.goods?.[i]?.dangerous ?? '')),
    h: [0, 1, 2].map((i) => isClosed(journey?.goods?.[i], journey?.goods?.[i]?.weights?.loaded)),
    i: [0, 1, 2].map((i) => isClosed(journey?.goods?.[i], journey?.goods?.[i]?.aspect ?? '')),
    l: journey.destination.full_name,
    m: journey.destination_type,
    n: [0, 1, 2].map((i) => (journey?.goods?.[i]?.weights?.unloaded === 0 ? 'X' : '')),
    o: [0, 1, 2].map((i) => journey?.goods?.[i]?.weights?.unloaded),
    p: journey.distance_total,
    q: journey.embarking_type, // Nave/Treno
    r: journey.embarking_from?.full_name,
    s: journey.embarking_to?.full_name,
    t: journey.crossed_countries?.map((c) => c.code).join('/') || '',
    u: journey._meta.journey_type,
    via: journey._meta.via,
  }
}

export const standardEmptyJourneyToRow = (journey) => {
  return {
    a: format(journey._meta.date, 'dd/MM'), // Data (gg/mm)
    b: journey.coupled_vehicle_load_capacity || '',
    c: journey.coupled_vehicle_axles || '',
    d: journey.origin.full_name,
    e: journey.origin_type,
    f: 0,
    g: '',
    h: '',
    i: '',
    l: journey.destination.full_name,
    m: journey.destination_type,
    n: '',
    o: 0,
    p: journey.distance_total,
    q: journey.embarking_type,
    r: journey.embarking_from?.full_name,
    s: journey.embarking_to?.full_name,
    t: journey.crossed_countries?.map((c) => c.code).join('/') || '',
    u: 4,
    via: journey._meta.via,
  }
}

export const roundJourneyToRow = (journey) => {
  return {
    a: format(journey._meta.date, 'dd/MM'), // Data (gg/mm)
    b: journey.origin.full_name,
    c: journey.destination.full_name,
    d: journey?._meta.journey_type === JourneyType3.ROUND_OR_SHUTTLE_EMPTY ? 0 : journey?.goods?.[0]?.type ?? '',
    e: journey?.goods?.[0]?.dangerous ?? '',
    f: journey?.goods?.[0]?.weight ?? '',
    g: journey?.goods?.[0]?.aspect ?? '',
    h: journey?._meta.journey_type === JourneyType3.ROUND_OR_SHUTTLE_EMPTY ? 0 : journey?.goods?.[1]?.type ?? '',
    i: journey?.goods?.[1]?.dangerous ?? '',
    l: journey?.goods?.[1]?.weight ?? '',
    m: journey?.goods?.[1]?.aspect ?? '',
    n: journey.distance_loaded,
    o: journey.distance_empty,
    p: journey.stops_count,
    q: journey.stops_province_count,
    r: journey.journeys_count,
    x: journey.coupled_vehicle === 1 ? journey.coupled_vehicle_load_capacity : '',
    y: journey.coupled_vehicle === 1 ? journey.coupled_vehicle_axles : '',
    z: journey.coupled_vehicle === 2 ? journey.coupled_vehicle_load_capacity : '',
    k: journey.coupled_vehicle === 2 ? journey.coupled_vehicle_axles : '',
    s: journey._meta.round_type,
    t: journey._meta.journey_type,
    via: journey._meta.via,
  }
}
